import ProductCard from "./components/ProductCard";
import "./styles.css";
import { NumberOfPeopleIcon, GiftCardIcon, OptionsIcon, NightIcon, HotelIcon, OnePersonIcon } from "./icons";
import { Hit, Product, AppProps } from "./types";

/**
 * App - Main application component for product tiles
 *
 * **Data Transformation:**
 * - Converts raw Algolia hit data to clean product format
 * - Handles business logic for pricing, location, and variants
 *
 * **Image Handling:**
 * - Creates demo images array if only one image provided
 * - Ensures carousel functionality with multiple images
 */
const App = ({ hit, filters }: AppProps) => {
  // Helper functions for data transformation

  const renderLocation = (hit: Hit): string | null => {
    const computedLocation = hit.meta?.custom?.computed_location;
    if (hit.product_type === "MLV") {
      const locationsCount = hit.meta?.custom?.dynamic_counts?.MLVLocationsCount;
      return locationsCount && locationsCount !== "0" ? `${locationsCount} ${locationsCount === "1" ? "Location" : "Locations"}` : null;
    }
    else if (computedLocation) {
      if (computedLocation?.short == "null, null" || computedLocation?.short == "Nationwide, undefine") {
        return "Nationwide";
      }
      else if (computedLocation?.short == "Virtual / At Home") {
        return "Online Experience";
      }
      
      return computedLocation?.short || computedLocation?.long || null;
    }
    return null;
  };

  const filterVariants = (filters: any, variants: any[]): any[] => {
    const priceFiltered = variants?.filter(
      (v) => filters.price.filter((n:any) => v.groupedPrice.includes(n)).length > 0
    );
    if (priceFiltered?.length > 0) variants = priceFiltered;
    const nbPersonsFiltered = variants?.filter(
      (v) =>
        filters.nbOfPersons.filter((n:any) => v.groupedNumberOfPersons.includes(n))
          .length > 0
    );
    if (nbPersonsFiltered?.length > 0) variants = nbPersonsFiltered;
    let finalVariants = variants.filter((v) => v.inventory_available);
    finalVariants = finalVariants.length > 1 ? finalVariants : variants;
    if (filters.sortBy.indexOf("price_desc") > -1)
      return finalVariants?.sort(
        (a, b) => parseFloat(a.price) - parseFloat(b.price)
      );
    return finalVariants?.sort(
      (a, b) => parseFloat(b.price) - parseFloat(a.price)
    );
  };

  const renderPrice = (filters: any, hit: Hit): number=> {
    if (hit.variants_count === 1 || !hit.meta?.search?.variants) return hit.price;

  if (
    hit.meta?.search?.variants
  ) {
    const variants = filterVariants(filters, [...hit.meta.search?.variants]);

    const selectedVariant = variants.pop();

    if (selectedVariant) return selectedVariant.price;
  }
  return hit.variants_min_price as number;
  };

  const renderCompareAtPrice = (filters: any, hit: Hit): number | undefined => {
    if (hit.variants_count === 1) return hit.compare_at_price;

  if (
    hit?.meta?.search?.variants
  ) {
    const variants = filterVariants(filters, [...hit?.meta.search?.variants]);
    const selectedVariant = variants?.pop();
    if (selectedVariant) return selectedVariant?.compare_at;
  }
  return hit.variants_compare_at_price_min 
  };

  const fixMapping = (hit: Hit): Hit => {
    
    if (!hit.meta?.search?.variants) return hit;

    hit.meta.search.variants = hit.meta.search?.variants.map((v) => {
      if (!Array.isArray(v.groupedNumberOfPersons)) {
        v.groupedNumberOfPersons = [v.groupedNumberOfPersons || ""];
      }
  
      if (!Array.isArray(v.groupedPrice)) {
        v.groupedPrice = [v.groupedPrice];
      }
  
      return v;
    });
  
    return hit;
  };

  const checkInventoryAvailability = (hit: Hit): boolean => {
    const isGiftoryNativeGiftCard = hit.id === "8288261177643";
    return isGiftoryNativeGiftCard || hit?.inventory_policy !== "deny";
  };

  // Helper functions for product transformation
  const processImages = (hit: Hit): string[] => {
    if (hit.images && hit.images.length > 0) {
      return hit.images.map((img) => img.url);
    }
    return [hit.product_image];
  };

  const processPricing = (hit: Hit, searchFilters: any) => {
    const price = renderPrice(searchFilters, hit);
    const originalPrice = renderCompareAtPrice(searchFilters, hit);
    
    // Calculate discount using numeric values
    let discount: string | undefined;
    if (originalPrice && originalPrice > price) {
      discount = `Save ${Math.round(((originalPrice - price) / originalPrice) * 100)}%`;
    }

    return { price, originalPrice, discount };
  };

  const processUspTags = (hit: Hit) => {
    // Don't show USP tags for hotel products
    if (hit.product_type === "HOTEL") {
      return undefined;
    }
    
    const allUspTags = hit.meta?.custom?.usp_tags || [];
    return allUspTags.length > 0
      ? allUspTags.reduce((prev, current) =>
          (current.displayPriority || 999) < (prev.displayPriority || 999)
            ? current
            : prev
        )
      : undefined;
  };

  const buildAttributes = (
    hit: Hit,
  ): Array<{ icon: any; name: string }> => {
    const attributes: Array<{ icon: any; name: string }> = [];

    const customData = hit.meta?.custom;
    const dynamicCountsData = customData?.dynamic_counts;
    const isPackagedProduct = customData?.packaged_product;
    const isGiftCard =
      customData?.experience_type === "Gift Card" ||
      hit.product_type === "GIFT_CARD";
    const isHotel = hit.product_type === "HOTEL";
    const isMCV = hit.product_type === "MCV";

    if (dynamicCountsData?.numberOfPeople && dynamicCountsData?.numberOfPeople !== "*" && dynamicCountsData?.numberOfPeople !== undefined && !isGiftCard && !isHotel) {
      attributes.push({
        icon: dynamicCountsData?.numberOfPeople === "1" ? <OnePersonIcon className="text-neutral-100" /> : <NumberOfPeopleIcon className="text-neutral-100" />,
        name: `${dynamicCountsData?.numberOfPeople} ${dynamicCountsData?.numberOfPeople === "1" ? "Person" : "People"}`,
      });
    }
    if (!isMCV && isPackagedProduct && dynamicCountsData?.packagedProductOptionsCount !== "0" && dynamicCountsData?.packagedProductOptionsCount !== undefined) {
      attributes.push({
        icon: <OptionsIcon className="text-neutral-100" />,
        name: `${dynamicCountsData?.packagedProductOptionsCount} Options`,
      });
    }
    if (isMCV && dynamicCountsData?.MCVProductsCount !== "0" && dynamicCountsData?.MCVProductsCount !== undefined) {
      attributes.push({
        icon: <OptionsIcon className="text-neutral-100" />,
        name: `${dynamicCountsData?.MCVProductsCount} Options`,
      });
    }
    if (isGiftCard) {
      attributes.push({
        icon: <GiftCardIcon className="text-neutral-100" />,
        name: `Gift Card`,
      });
    }
    if (isHotel) {
      if (dynamicCountsData?.SLVHotelNumberOfNightsCount && dynamicCountsData?.SLVHotelNumberOfNightsCount !== undefined) {
        attributes.push({
          icon: <NightIcon className="text-neutral-100" />,
          name: `${dynamicCountsData?.SLVHotelNumberOfNightsCount}`,
        });
      }
      if (dynamicCountsData?.SLVHotelCount && dynamicCountsData?.SLVHotelCount !== "0" && dynamicCountsData?.SLVHotelCount !== undefined) {
        attributes.push({
          icon: <HotelIcon className="text-neutral-100" />,
          name: `${dynamicCountsData?.SLVHotelCount} Hotels`,
        });
      }
    }
    return attributes;
  };

  // Transform hit data to product format //
  const transformHitToProduct = (hit: Hit): Product => {
    const processedHit = fixMapping(hit);
    const searchFilters = filters || { price: [], nbOfPersons: [] };

    const { price, originalPrice, discount } = processPricing(
      processedHit,
      searchFilters
    );
    const images = processImages(hit);
    const isAvailable = checkInventoryAvailability(hit);
    const highestPriorityUspTag = processUspTags(processedHit);
    const attributes = buildAttributes(processedHit);

    // Get actual review data from meta.custom.reviewStats
    const reviewStats = processedHit.meta?.custom?.dynamic_counts?.reviewStats;
    const rawRating = reviewStats?.averageRating;
    const rating = rawRating !== undefined && rawRating !== null 
      ? (Number.isInteger(rawRating) ? rawRating.toFixed(1) : rawRating)
      : rawRating;
    const reviews = reviewStats?.total;
    const priceString = price % 1 === 0 ? Math.floor(price).toString() : price.toFixed(2);
    const originalPriceString = originalPrice 
      ? (originalPrice % 1 === 0 ? Math.floor(originalPrice).toString() : originalPrice.toFixed(2))
      : undefined;
    return {
      id: hit.id.toString(),
      title: hit.title,
      price: priceString,
      originalPrice: originalPriceString,
      images,
      handle: hit.handle,
      location: renderLocation(processedHit),
      rating,
      reviews,
      attributes: attributes || [],
      discount,
      uspTags: highestPriorityUspTag ? [highestPriorityUspTag] : [],
      isAvailable,
    };
  };

  const product = hit && transformHitToProduct(hit);

  return (
    <div className="w-full h-full">
      {product && <ProductCard product={product} />}
    </div>
  );
};

export default App;
